import { BoundingBox, CategorizedData, DisaggregatedMetric, GeoDetails, GeoTag, Metric, MetricMetadata } from "../services/types";

import populationSvg from '../assets/pop-blue.svg';
import popSvg from '../assets/pop-purp.svg';
import limitedEngSvg from '../assets/limitedenglish.svg';
import povertySvg from '../assets/povertyrate.svg';
import hhincomeSvg from '../assets/hhincome.svg';
import employmentSvg from '../assets/employment.svg';
import walkabilitySvg from '../assets/walkabilityindex.svg';
import briefcaseSvg from '../assets/nonautojobs.svg';
import housingCostSvg from '../assets/housing-cost-burden.svg';
import { indicatorsEnv, metricsEnv } from "./environment";

// Store our organization Id where we can access it later
export let orgId: number = 10;

// #region Organization Metric Mapping
const organizationMetrics: { [orgId: number]: any[] } =
{ 
    // Memphis
    10:
    [
        {
            name: "Demographic",
            ids: [362, 363, 364, 367, 368, 373],
            order: [
                [362, 368, 364],
            ],
            charts: [
                {
                    name: "hhincome",
                    id: 373
                },
                {
                    name: "race",
                    id: 367
                }
            ]
        },
        {
            name: "Livability",
            ids: [366, 369, 370, 371],
            order: [
                [366, 371, 369, 370],
            ],
            charts: []
        },
        {
            name: "Economy",
            ids: [372],
            order: [
                [],
            ],
            charts: [
                {
                    name: "industry",
                    id: 372
                }
            ]
        },
        {
            name: "Land use",
            ids: [],
            order: [
                
            ],
            charts: []
        }
    ],
    
};

// precisionMapping - maps metricId's to the desired number of significant figures for formatting metric values
export const precisionMapping: { [metricId: number]: number } = {
    368: 0,
    364: 1,
    369: 0,
    370: 0,
};


const svgMap: { [orgId: number]: { [key: number]: string }} =
{
    // Memphis
    10: {
        362: populationSvg, // Population
        368: limitedEngSvg,
        364: povertySvg,
        367: popSvg,
        363: hhincomeSvg, 
        372: employmentSvg,
        366: walkabilitySvg,
        371: briefcaseSvg,
        369: housingCostSvg,
        370: housingCostSvg,
    },
    
};

// There are svgs that need to be included in the pdf that are excluded in the dashboard
const pdfSvgMap: { [orgId: number]: { [key: number]: string }} =
{
    // Memphis
    10: {
        362: populationSvg, // Population
        368: limitedEngSvg,
        364: povertySvg,
        367: popSvg,
        363: hhincomeSvg, 
        372: employmentSvg,
        366: walkabilitySvg,
        371: briefcaseSvg,
        369: housingCostSvg,
        370: housingCostSvg,
    },
    
};

// #endregion



// getMetricGroupIds - get the metric id values for a specific grouping 
export const getMetricGroupIds = (name: string) => {

    let ids = [];
    
    if(organizationMetrics[orgId] != undefined){
        const group: any = organizationMetrics[orgId].find((x: { name: string; }) => x.name === name);

        if(group != undefined){
            ids = group.ids;
        }
    }

    return ids;
}

// Store our organization Id where we can access it later
export const setOrgId = (id: number) => {
    orgId = id;
}

// Store our overlapping county fips codes for BLS and TRS data queries
export let countyId: string[] = [];
export const setCountyIds = (id: string[]) => {
    countyId = id;
}
export const getCountyIds = () => {
    return countyId;
}

// Store our bounding box so we can access it later
export let boundingBox: BoundingBox | null = null;
export const setBoundingBox = (bb: BoundingBox) => {
    boundingBox = bb;
}

// Store our excludeIds so we can access them later
export let excludeIds: string[] | null = null;
export const setExcludeIds = (ids: string[]) => {
    excludeIds = ids;
}

// getSvgMap - get our svg mapping for the given organization and metric id
export const getSvgMap = (id: number) => {
    let svg = "";
    if(svgMap[orgId] != undefined){
        svg = svgMap[orgId][id];
    }
    return svg;
}

// getPdfSvgMap - get our pdf svg mapping for the given organization and metric id
export const getPdfSvgMap = (id: number) => {
    let svg = "";
    if(pdfSvgMap[orgId] != undefined){
        svg = pdfSvgMap[orgId][id];
    }
    return svg;
}

// getMetricGroupOrderIds - get the ordering for a metric group
export const getMetricGroupOrderIds = (name: string) => {
    let orderIds = [];
    
    if(organizationMetrics[orgId] != undefined){
        const group: any = organizationMetrics[orgId].find((x: { name: string; }) => x.name === name);

        if(group != undefined){
            orderIds = group.order;
        }
    }

    return orderIds;
}

// getMetricGroupChartId - get a metric id given the group and chart key
export const getMetricGroupChartId = ( group: string, key: string) => {
    let id = -1;

    if(organizationMetrics[orgId] != undefined){
        const g = organizationMetrics[orgId].find((x: { name: string; }) => x.name === group);
        if(g != undefined){
            
            if(g.charts != undefined){

                const chart = g.charts.find((x: { name: string; }) => x.name === key);
                if(chart != undefined){
                    id = chart.id;
                }
            }
        }
    }

    return id;
}

export const categorizeData = (indicators: DisaggregatedMetric[]) => {

    const categories: { [key: string]: number[] } = {
        Livability: getMetricGroupIds("Livability"),
        Economy: getMetricGroupIds("Economy"), 
        "Land use": getMetricGroupIds("Land use"),
        Demographic: getMetricGroupIds("Demographic"),
    };

    // Initialize the categorizedData object dynamically with empty arrays for each category
    const categorizedData: Record<string, Metric[]> = Object.keys(categories).reduce((acc, category) => {
        acc[category] = [];
        return acc;
    }, {} as Record<string, Metric[]>);

    // Categorize the indicators dynamically
    indicators.forEach((item) => {
        Object.keys(categories).forEach((category) => {
            if (categories[category].includes(item.id)) {
                categorizedData[category].push(item);
            }
        });
    });

    return categorizedData;
};

export const fetchMetricCategories = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetriccategories?orgId=${orgId}&environment=${metricsEnv}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadata = async (id: number) => {
    if (!id) {
        console.log('No id provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetricmetadata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id, orgId, environment: metricsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadataByOrg = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetadatabyorg`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgId, environment: metricsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

// fetch PercentileDist - get percentile distribution information by org and category
export const fetchPercentileDist = async (orgId: number, category: string) => {
    if (!orgId || !category) {
        console.log('No orgId or category provided to fetch metric percentile distributions');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getpercentiledist?orgId=${orgId}&category=${category}&environment=${indicatorsEnv}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

// fetchPercentileById - get percentile distribution information by metric id
export const fetchPercentileById = async (metricId: number) => {
    if (!metricId) {
        console.log('No metricId provided to fetch metric percentile data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getpercentilebymetric?metricId=${metricId}&orgId=${orgId}&environment=${indicatorsEnv}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching percentile data:', error);
    }
};

// fetchDistributionData - get distribution data by metric id and selected area
export const fetchDistributionData = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getdistributiondata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, orgId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchIndicatorBinCount - get indicator data and bin count by metric id and selected area
export const fetchIndicatorBinCount = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getindicatorbincounts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, orgId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchStateData - retreive state data for a selected metric using a prefix state code
export const fetchStateData = async (metricId: number, geoPrefix: string) => {
    if (!metricId || !geoPrefix) {
        console.log('No metricId or geoDetails provided to fetch metric state data');
        return;
    }

    console.log('geoprefix:', geoPrefix);

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, orgId, geoPrefix, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric state data:', error);
    }
};

// fetchNationalData - retreive national data for a selected metric
export const fetchNationalData = async (metricId: number) => {
    if (!metricId) {
        console.log('No metricId provided to fetch national metric data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, orgId, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching national metric data:', error);
    }
};

export const calculateGeoPrefix = (geoDetails: GeoDetails[]): string => {
    // Flatten the geoIds array
    const geoIds = geoDetails.flatMap(geo => geo.ids);

    // Helper function to get the first two digits of a geoId
    const getGeoPrefix = (geoId: string): string => geoId.substring(0, 2);

    // Count occurrences of each prefix
    const prefixCount: Record<string, number> = {};
    geoIds.forEach(geoId => {
        const prefix = getGeoPrefix(geoId);
        prefixCount[prefix] = (prefixCount[prefix] || 0) + 1;
    });

    // Find the prefix with the highest count
    return Object.entries(prefixCount).reduce((prev, curr) => 
        curr[1] > prev[1] ? curr : prev, ['', 0]
    )[0];
};


// getMissingMetrics - utility function to check for missing metrics on a given dashboard panel
export const getMissingMetrics = (
    tab: string,
    categorizedData: Metric[],
    indicators: DisaggregatedMetric[] | null
  ): string => {
    if (!indicators) return '';
  
    const missingMetrics = indicators?.filter(metric => 
        metric.category?.toLowerCase() === tab.toLowerCase() && 
        !categorizedData.some(data => data.id === metric.id)
    ) || [];    
  
    return missingMetrics.map(metric => metric.name).join(", ");
};

// Helper function to get and sort metrics by category
export const getSortedMetrics = (category: string, categorizedData: CategorizedData) => {
    const orders = getMetricGroupOrderIds(category);

    return orders.map((order: number[]) => 
        categorizedData[category]
            .filter(metric => order.includes(metric.id))
            .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
};

// Helper function to get and sort metrics by category - Used in PDF metrics display
export const getSortedMetricsPDF = (category: string, categorizedData : {
    category: string;
    metrics: DisaggregatedMetric[];
}[]): DisaggregatedMetric[] => {
    const metrics = categorizedData.find(data => data.category === category)?.metrics || [];
    const orders = getMetricGroupOrderIds(category);
    return orders.flatMap((order: number[]) =>
      metrics
        .filter(metric => order.includes(metric.id))
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
};
  
// Helper function to get the index metric data
export const getIndexMetricData = (
    category: string, 
    metadata: MetricMetadata[] | null, 
    disaggregatedData: DisaggregatedMetric[]
): { indexMetricId: number; indexMetricData: DisaggregatedMetric | null } => {
    
    const indexMetricId = metadata?.find(
        (metric) => 
            metric.metric_source_type === "index" && 
            metric.metric_category.toLowerCase() === category.toLowerCase()
    )?.id || 0;

    const indexMetricData = disaggregatedData.find(metric => metric.id === indexMetricId) || null;

    return { indexMetricId, indexMetricData };
};

// Helper function to get the index metric data - Formatted for PDF (MetricModal) use.
export const getIndexMetricDataPDF = (category: string, metadata: MetricMetadata[] | null, disaggregatedData: DisaggregatedMetric[]): DisaggregatedMetric | null => {
    const indexMetricId = metadata?.find(
        (metric) => metric.metric_source_type === "index" && metric.metric_category.toLowerCase() === category.toLowerCase()
    )?.id || 0;
    return disaggregatedData.find(metric => metric.id === Number(indexMetricId)) || null;
};
